import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useMemo } from 'react';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/Pathways/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES, preparePayload } from 'pages/Pathways/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import ComponentPreview from 'components/PreviewWrapper';
import { useGetEvent } from 'api/events';
import FormWrapper from 'components/FormWrapper';

function EventPathwayCreate() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const backPath = useMemo(
    () =>
      generatePath(ROUTES.events.pathways.base, {
        id,
      }),
    [id],
  );

  const methods = useForm<Pathway>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError } = methods;

  const { data: { data: event } = {} } = useGetEvent(+id);

  const { mutate: createPathway, isLoading } = useMutation(
    (data: Pathway) =>
      API.post(APIRoutes.events.pathways.index(+id), {
        pathway: preparePayload(data),
      }),
    {
      mutationKey: 'eventPathwaysMutation',
      onSuccess: () => {
        toast.success('Ścieżka zwiedzania została pomyślnie utworzona!');
        navigate(backPath);
        void queryClient.invalidateQueries('eventPathways');
      },
      onError: (error) => {
        setFormErrors(setError, error);
        toast.error('Wystąpił problem podczas tworzenia ścieżki');
      },
    },
  );

  const onSubmit = handleSubmit((data: Pathway) => createPathway(data));

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: event?.name ?? '',
          to: backPath,
        }}
        crumbs={[{ to: '', label: 'Nowa ścieżka' }]}
      />
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="pathway-creator" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} id={+id} isEventsForm />
          </Box>
          <ComponentPreview kind="pathway" />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} isCreate formId="pathway-creator" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default EventPathwayCreate;
